/**
 * Parasocial Metrics Display Component
 *
 * Displays Parasocial Detection metrics for simulation runs.
 * Shows effectiveness score, signals detected, interventions triggered, and other metrics.
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  LinearProgress,
  Tooltip
} from '@mui/material';
import {
  Psychology as ParasocialIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Visibility as DetectionIcon,
  Notifications as InterventionIcon
} from '@mui/icons-material';

/**
 * Get color based on effectiveness score (higher = better = fewer issues)
 */
const getEffectivenessColor = (score) => {
  if (score >= 80) return 'success';
  if (score >= 50) return 'warning';
  return 'error';
};

/**
 * Get color for signal count
 */
const getSignalCountColor = (count) => {
  if (count === 0) return 'success.main';
  if (count <= 3) return 'warning.main';
  return 'error.main';
};

/**
 * Map phase to readable label
 */
const getPhaseLabel = (phase) => {
  const labels = {
    'none': 'None',
    'observation': 'Observation',
    'soft_disclosure': 'Soft Disclosure',
    'active_redirect': 'Active Redirect',
    'explicit_disclosure': 'Explicit Disclosure'
  };
  return labels[phase] || phase;
};

/**
 * Get phase severity color
 */
const getPhaseSeverityColor = (phase) => {
  if (phase === 'none' || phase === 'observation') return 'default';
  if (phase === 'soft_disclosure') return 'info';
  if (phase === 'active_redirect') return 'warning';
  return 'error';
};

/**
 * Category labels for display
 */
const categoryLabels = {
  exclusivity_language: 'Exclusivity',
  romantic_projection: 'Romantic',
  ai_abandonment_anxiety: 'Abandonment',
  crisis_dependency: 'Crisis Dep.',
  delusional_attribution: 'Delusional',
  social_isolation: 'Isolation'
};

/**
 * Parasocial Metrics Display Component
 * @param {Object} props
 * @param {Object} props.parasocialMetrics - Parasocial metrics object from simulation run
 * @param {Object} props.parasocialConfig - Parasocial configuration used for the run
 * @param {boolean} props.compact - Whether to show compact view
 */
function ParasocialMetricsDisplay({ parasocialMetrics, parasocialConfig, compact = false }) {
  // If parasocial wasn't enabled or no metrics, show disabled state
  if (!parasocialConfig?.enabled || !parasocialMetrics) {
    return (
      <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
        <CardContent sx={{ py: compact ? 1.5 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ParasocialIcon color="disabled" fontSize={compact ? 'small' : 'medium'} />
            <Typography variant={compact ? 'body2' : 'body1'} color="text.secondary">
              Parasocial Detection Disabled
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  const {
    parasocialEffectivenessScore = 100,
    totalSignalsDetected = 0,
    maxAttachmentScore = 0,
    avgAttachmentScore = 0,
    signalsByCategory = {},
    interventionCount = 0,
    interventionPhases = {},
    maxInterventionPhase = 'none'
  } = parasocialMetrics;

  const effectivenessColor = getEffectivenessColor(parasocialEffectivenessScore);

  // Compact view for list displays
  if (compact) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Tooltip title="Parasocial Effectiveness Score">
          <Chip
            icon={<ParasocialIcon />}
            label={`${parasocialEffectivenessScore}/100`}
            size="small"
            color={effectivenessColor}
          />
        </Tooltip>
        <Tooltip title="Signals Detected">
          <Chip
            icon={<DetectionIcon />}
            label={`${totalSignalsDetected} signals`}
            size="small"
            variant="outlined"
            color={totalSignalsDetected > 3 ? 'warning' : 'default'}
          />
        </Tooltip>
        {interventionCount > 0 && (
          <Tooltip title="Interventions Triggered">
            <Chip
              icon={<InterventionIcon />}
              label={`${interventionCount} interventions`}
              size="small"
              color="info"
              variant="outlined"
            />
          </Tooltip>
        )}
        {maxInterventionPhase !== 'none' && maxInterventionPhase !== 'observation' && (
          <Tooltip title={`Max Intervention Phase: ${getPhaseLabel(maxInterventionPhase)}`}>
            <Chip
              icon={<WarningIcon />}
              label={getPhaseLabel(maxInterventionPhase)}
              size="small"
              color={getPhaseSeverityColor(maxInterventionPhase)}
            />
          </Tooltip>
        )}
      </Box>
    );
  }

  // Full view with detailed metrics
  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <ParasocialIcon color="secondary" />
          <Typography variant="h6">
            Parasocial Metrics
          </Typography>
          <Chip
            label={parasocialConfig.disclosureMode || 'Default'}
            size="small"
            color="secondary"
            variant="outlined"
            sx={{ ml: 'auto' }}
          />
        </Box>

        <Grid container spacing={2}>
          {/* Effectiveness Score */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h3" color={`${effectivenessColor}.main`}>
                {parasocialEffectivenessScore}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Effectiveness Score
              </Typography>
              <LinearProgress
                variant="determinate"
                value={parasocialEffectivenessScore}
                color={effectivenessColor}
                sx={{ mt: 1, height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>

          {/* Signals Detected */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h3" sx={{ color: getSignalCountColor(totalSignalsDetected) }}>
                {totalSignalsDetected}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Signals Detected
              </Typography>
            </Box>
          </Grid>

          {/* Max Attachment Score */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4" color={maxAttachmentScore > 0.6 ? 'error.main' : 'text.primary'}>
                {(maxAttachmentScore * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Max Score
              </Typography>
            </Box>
          </Grid>

          {/* Avg Attachment Score */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4">
                {(avgAttachmentScore * 100).toFixed(0)}%
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Avg Score
              </Typography>
            </Box>
          </Grid>

          {/* Intervention Count */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Typography variant="h4" color={interventionCount > 3 ? 'warning.main' : 'text.primary'}>
                {interventionCount}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Interventions
              </Typography>
            </Box>
          </Grid>

          {/* Max Phase */}
          <Grid item xs={6} sm={3} md={1.5}>
            <Box sx={{ textAlign: 'center', p: 1 }}>
              <Chip
                label={getPhaseLabel(maxInterventionPhase)}
                size="small"
                color={getPhaseSeverityColor(maxInterventionPhase)}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Max Phase
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Alert Indicators */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {maxInterventionPhase === 'explicit_disclosure' && (
            <Chip
              icon={<WarningIcon />}
              label="Explicit Disclosure Required"
              color="error"
              size="small"
            />
          )}
          {totalSignalsDetected > 5 && (
            <Chip
              icon={<DetectionIcon />}
              label="High Signal Count"
              color="warning"
              size="small"
            />
          )}
          {parasocialEffectivenessScore >= 90 && totalSignalsDetected === 0 && (
            <Chip
              icon={<SuccessIcon />}
              label="Healthy Attachment Patterns"
              color="success"
              size="small"
            />
          )}
        </Box>

        {/* Signals By Category */}
        {totalSignalsDetected > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Signals by Category
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {Object.entries(signalsByCategory)
                .filter(([_, count]) => count > 0)
                .map(([category, count]) => (
                  <Chip
                    key={category}
                    label={`${categoryLabels[category] || category}: ${count}`}
                    size="small"
                    variant="outlined"
                    color={count > 2 ? 'error' : 'default'}
                  />
                ))}
            </Box>
          </Box>
        )}

        {/* Intervention Phase Breakdown */}
        {interventionCount > 0 && (
          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Intervention Phases Used
            </Typography>
            <Grid container spacing={1}>
              {interventionPhases.softDisclosure > 0 && (
                <Grid item>
                  <Chip
                    label={`Soft Disclosure: ${interventionPhases.softDisclosure}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                </Grid>
              )}
              {interventionPhases.activeRedirect > 0 && (
                <Grid item>
                  <Chip
                    label={`Active Redirect: ${interventionPhases.activeRedirect}`}
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                </Grid>
              )}
              {interventionPhases.explicitDisclosure > 0 && (
                <Grid item>
                  <Chip
                    label={`Explicit Disclosure: ${interventionPhases.explicitDisclosure}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

export default ParasocialMetricsDisplay;
